import React, { useEffect, useReducer, useRef } from 'react';
import { Helmet } from 'react-helmet';
// styles
import { header } from './styles/form';
import { MultiFactorAuthContainer } from './styles/multiFactorPage';
// services
import { auth } from 'js/common/services/api';
import deviceCodeReducer from 'js/auth/reducers/deviceCodeReducer';
// context
import { useAccountUser } from 'js/auth/context/AccountUserContext';
import { useRequestParams } from 'js/common/context/RequestParamsContext';
// components
import { H2 } from '@leafygreen-ui/typography';
import AutoAdvanceInputs from 'js/common/components/AutoAdvanceInputs';
import Card from '@leafygreen-ui/card';
import HeaderLogo from './HeaderLogo';
import Layout from './Layout';
import { DeviceAuthInput } from './DeviceAuthInput';
import { DeviceAuthConfirmation } from './DeviceAuthConfirmation';

// styles

interface DeviceAuthPageProps {
  location?: Pick<Location, 'search'>;
  windowLocation: Pick<Location, 'assign'>;
}

export function DeviceAuthPage({
  location: { search } = { search: '' },
  windowLocation = window.location,
}: DeviceAuthPageProps) {
  const { accountCentralUrl } = useRequestParams();
  const { accountUserParams, loadAccountUserParams } = useAccountUser();
  const { failedToLoad, firstName, lastName, username } = accountUserParams;
  const autoAdvanceInputsRef = useRef<AutoAdvanceInputs>(null);

  const [
    { sourceIp, sourceUserAgent, icon, error, sessionId, createdAt, deviceCodeId, deviceCodeStatus },
    fetchDispatch,
  ] = useReducer(deviceCodeReducer, {
    deviceCodeId: '',
    userCode: '',
    sessionId: '',
    sourceIp: '',
    sourceUserAgent: '',
    createdAt: '',
    icon: 'spinner',
    deviceCodeStatus: 'LOADING',
  });
  useEffect(() => {
    async function loadAuthParams() {
      await loadAccountUserParams();
    }

    loadAuthParams();

    if (failedToLoad) {
      windowLocation.assign(`/account/login?fromURI=${accountCentralUrl}/account/connect`);
    } else {
      fetchDispatch({
        type: 'onLoad',
      });
    }
  }, [accountCentralUrl, windowLocation, failedToLoad, loadAccountUserParams]);

  function onComplete({ value }) {
    if (value == null || value.length !== 8) {
      autoAdvanceInputsRef?.current?.reset();
      fetchDispatch({
        type: 'onCompleteError',
        payload: {
          error: 'invalid code',
        },
      });
      return;
    }
    auth
      .verifyDeviceCode({ userCode: value })
      .then((resp) => {
        fetchDispatch({
          type: 'onPostComplete',
          payload: {
            deviceCodeId: resp.deviceCode,
            userCode: value,
            sessionId: resp.sessionId,
            sourceIp: resp.sourceIp,
            sourceUserAgent: resp.sourceUserAgent,
            createdAt: resp.createdAt,
          },
        });
      })
      .catch((e) => {
        autoAdvanceInputsRef?.current?.reset();
        fetchDispatch({
          type: 'onCompleteError',
          payload: {
            error: e,
          },
        });
      });
  }

  function onConfirm() {
    auth
      .confirmDeviceCode({ deviceCode: deviceCodeId, sessionId })
      .then(({ loginRedirectUrl }) => {
        windowLocation.assign(loginRedirectUrl);
      })
      .catch((e) => {
        autoAdvanceInputsRef?.current?.reset();
        fetchDispatch({
          type: 'onCompleteError',
          payload: {
            error: e,
          },
        });
      });
  }

  function onBack() {
    fetchDispatch({
      type: 'onBack',
    });
    autoAdvanceInputsRef?.current?.reset();
  }

  function getCardContent() {
    switch (deviceCodeStatus) {
      case 'INPUT':
        return (
          <DeviceAuthInput
            username={username}
            firstName={firstName}
            lastName={lastName}
            icon={icon}
            onComplete={onComplete}
            autoAdvanceInputsRef={autoAdvanceInputsRef}
            error={error}
          />
        );
      case 'CONFIRMING':
        return (
          <DeviceAuthConfirmation
            sourceIp={sourceIp}
            sourceUserAgent={sourceUserAgent}
            createdAt={createdAt}
            onConfirm={onConfirm}
            onBack={onBack}
          />
        );
      default:
        return <div />;
    }
  }

  return (
    <>
      <Helmet title="Activation" />
      <Layout contentPlacement="right">
        <MultiFactorAuthContainer>
          <HeaderLogo />
          <H2 css={header}>Activation</H2>
          <Card css={{ minHeight: 200, maxWidth: 500 }}>{getCardContent()}</Card>
        </MultiFactorAuthContainer>
      </Layout>
    </>
  );
}
